// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159808 $
// Purpose     : react components for vl type system
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Checkbox, Image, Menu, Icon, Button, Segment, Table} from 'semantic-ui-react'
import clsx from 'clsx'

// ---Framework imports
import {
  keys,
  isNil,
  uniq,
  get,
  strip,
  isString,
  isInteger,
  mapItems,
  sorted,
  values,
  flatten,
  isObject,
  groupBy,
  dict,
  size,
} from 'ldash'
import log from 'uix/logging'
import {
  useSelector,
  usePersistent,
  useGraphics,
  useState,
  useEffectSSR,
  useSummaries,
  useMemo,
  useApi,
} from 'uix/hooks'
import {Sel} from 'uix/registries'
import {ucFirst} from 'uix/utils'
import {ResizableDialog, Link, RGrid, XMenu} from 'uix/components'
import {Graphics} from 'uix/graphics'
import {DomainLabel} from 'uix/domain/label'

// ---Local imports
import typeStyles from './styles.scss'

// ---Exports

export const useChosenTypes = () => {
  /* usePersistent with query (&types=...) parameter override support
   */
  let request = useSelector(Sel.request)
  let [from_query_initialized, set_from_query_initialized] = useState(false)
  let [value, set_value] = usePersistent('TypeChooser', [])
  let decode = qt => qt.split(',').filter(e => e)
  useEffectSSR(() => {
    // initialization phase
    let qt = request.query.types
    if (qt !== undefined) {
      qt = qt || ''
      if (qt != value.join(',')) {
        // update persistence
        set_value(decode(qt))
      }
    }
    // switch to persisted value now
    // XXX: what if set_from_query_initialized() is faster than set_value() ?
    if (!from_query_initialized) set_from_query_initialized(true)
  }, [request])

  if (request.query.types !== undefined && !from_query_initialized) {
    // return decoded query param during initialization
    return [decode(request.query.types || ''), set_value]
  }
  return [value, set_value]
}

const defaultDatatypeNames = dconfig =>
  [
    'topstructure',
    'metadata',
    'substructure',
    'page',
    dconfig.names.on && 'name',
    dconfig.places.on && 'place',
    dconfig.subjects.on && 'subject',
  ].filter(e => e)

// useChosenTypes hook is not usable in class based part
// so hack a selector
export const selectTypeNames = (root, partName) =>
  get(root.ui.byId, `Part/${partName}/TypeChooser.value`, [])

export const TypeChooser = ({disabled, filteredDatatype, subdomains}) => {
  let graphics = useGraphics(['types'])
  let dconfig = useSelector(Sel.dconfig)
  let dataTypes = filteredDatatype ? [filteredDatatype] : defaultDatatypeNames(dconfig)

  let [open, setOpen] = useState(false)

  let types = useSelector(Sel.types)
  let {data} = useApi('types/getSubdomainTypes', {}, {cached: true, condition: open && subdomains})

  types = useMemo(() => {
    let sdtypes = data?.subdomainTypes || {}
    return dict(dataTypes.map(dt => [dt, uniq([...keys(types[dt]), ...(sdtypes[dt] ?? [])])]))
  }, [types, data])

  let i18n = useSelector(Sel.i18n)

  let [typeNames, setTypeNames] = useChosenTypes()
  let [activeTypes, setActiveTypes] = useState(typeNames)
  let [closedGroups, setClosedGroups] = usePersistent(
    'TypeChooserClosed',
    defaultDatatypeNames(dconfig),
    2
  )
  closedGroups = isNil(filteredDatatype) ? closedGroups : []

  let onCancel = () => {
    setActiveTypes(typeNames)
    setOpen(false)
  }

  let toggle = val => {
    if (activeTypes.includes(val)) {
      activeTypes = activeTypes.filter(n => n !== val)
    } else {
      activeTypes = [...activeTypes, val]
    }
    setActiveTypes(activeTypes)
  }

  let countActive = datatype => activeTypes.filter(n => types[datatype].includes(n)).length
  let countAvailable = datatype => size(types[datatype])

  let clearGroup = datatype =>
    setActiveTypes(activeTypes.filter(n => !types[datatype].includes(n) && n !== `_${datatype}_`))

  let toggleOpenGroup = val => {
    if (closedGroups.includes(val)) {
      closedGroups = closedGroups.filter(n => n !== val)
    } else {
      closedGroups = [...closedGroups, val]
    }
    setClosedGroups(closedGroups)
  }

  // add datatypes with len(subtypes) == 0
  let closedGroupsAndEmpty = [...closedGroups, ...dataTypes.filter(dt => countAvailable(dt) == 0)]

  let groups_sorted = sorted(dataTypes, {
    //~ key: child => i18n.get(`types_datatype_${child}`),
    key: dt => [2, 1, 0, 3, 13, 16, 19].indexOf(dt), // natural order, the 3 gndo types to the end
    localcmp: true,
  })

  let groups = groups_sorted.map(datatype => {
    let children = types[datatype].map(child => (
      <Checkbox
        key={child}
        style={{width: '250px', marginTop: '.5rem'}}
        id={child}
        checked={activeTypes.includes(child) || activeTypes.includes(`_${datatype}_`)}
        disabled={activeTypes.includes(`_${datatype}_`)}
        onChange={(e, data) => toggle(child)}
        label={
          <label htmlFor={child}>
            {graphics.types && (
              <Image
                className={typeStyles.labelImage}
                src={graphics.types[`${datatype}/${child}`]}
              />
            )}
            <span>{i18n.getType(child)}</span>
          </label>
        }
      />
    ))
    let children_sorted = sorted(children, {
      key: child => i18n.getType(child.key),
      localcmp: true,
    })
    let groupActive = activeTypes.includes(`_${datatype}_`)
    let indeterminate = !groupActive && countActive(datatype) > 0
    return (
      <div
        className={clsx(
          typeStyles.group,
          closedGroupsAndEmpty.includes(datatype) && typeStyles.closed
        )}
        key={datatype}
      >
        <div className={typeStyles.headline}>
          <Checkbox
            checked={groupActive}
            indeterminate={indeterminate}
            label={
              <label>
                <DatatypeDescription name={datatype} />
                {indeterminate ? ` (${countActive(datatype)})` : null}
              </label>
            }
            style={{color: 'grey'}}
            onClick={() => toggle(`_${datatype}_`)}
          />
          <XMenu padded size="tiny">
            <XMenu.Item
              icon="eraser"
              onClick={() => clearGroup(datatype)}
              size="tiny"
              title="Clear"
              disabled={!groupActive && countActive(datatype) < 0}
            />
            <XMenu.Item
              icon={closedGroupsAndEmpty.includes(datatype) ? 'chevron down' : 'chevron up'}
              onClick={() => toggleOpenGroup(datatype)}
              disabled={countAvailable(datatype) == 0}
            />
          </XMenu>
        </div>
        {!closedGroupsAndEmpty.includes(datatype) && (
          <div className={typeStyles.content}>{children_sorted}</div>
        )}
      </div>
    )
  })

  return (
    <>
      <Menu.Item
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={`${i18n.get('datatype')}`}
      >
        <Icon.Group>
          <Icon name="filter" /> <Icon corner name="folder outline" />
        </Icon.Group>
        <DatatypeIcons activeTypes={activeTypes} />
      </Menu.Item>
      {open && (
        <ResizableDialog
          closeOnEsc
          onClose={() => {
            setOpen(false)
          }}
          onCancel={onCancel}
          onSubmit={() => {
            setTypeNames(activeTypes)
            setOpen(false)
          }}
          icon={'filter'}
          header={i18n.get('datatype')}
          children={<div style={{padding: '1rem'}}>{groups}</div>}
        />
      )}
    </>
  )
}
const DatatypeIcons = ({activeTypes}) => {
  let itypes = useSelector(Sel.itypes)
  let graphics = useGraphics(['types'])

  let graphic_names = uniq(
    activeTypes.map(typeName => {
      let dt = itypes.byName[typeName]?.[0]
      if (typeName.startsWith('_')) {
        dt = itypes.datatypeMapInverse[strip(typeName, '_')]
      }
      return `${dt}_0`
    })
  )
  let icon = n => (
    <Image style={{marginLeft: 3}} key={n} inline width="14px" src={graphics.types?.[n]} />
  )
  return graphic_names.map(icon)
}

export const TypeIcon = ({name, datatype, subtype, title, width = 14, style}) => {
  let s = name ? name : `${datatype}_${subtype}`
  return (
    <Image
      title={title}
      inline
      style={style}
      width={width}
      src={Graphics.types && Graphics.types[s]}
      className={typeStyles.icon}
    />
  )
}

// type icon + custom label
export const TypeLabel = ({content, children, style, as = 'span', icon = true, title, ...props}) =>
  React.createElement(as, {
    style,
    title,
    children: (
      <>
        {icon && <TypeIcon {...props} />} {content || children}
      </>
    ),
  })

// datatype icon + datatype translation
export const DatatypeDescription = ({name, datatype, unlabeled = false, ...props}) => {
  let i18n = useSelector(Sel.i18n)
  let itypes = useSelector(Sel.itypes)
  if (!isNil(datatype)) {
    name = itypes.datatypeMap[datatype]
  }
  let label = i18n.get(`types_datatype_${name}`)
  if (unlabeled) {
    props = {...props, title: label}
  } else {
    props = {...props, content: <span>{label}</span>}
  }
  return <TypeLabel {...props} name={name} />
}

// type icon + type translation
export const TypeDescription = ({name, datatype, subtype, ...props}) => {
  let i18n = useSelector(Sel.i18n)
  props = {...props, name, datatype, subtype}
  return <TypeLabel {...props} content={i18n.getType(name || datatype, subtype)} />
}

// node type icon + node.caption (with fallback to TypeDescription on empty caption)
export const TypeCaption = ({caption, ...props}) =>
  caption ? <TypeLabel content={caption} {...props} /> : <TypeDescription {...props} />

export const DomainNode = ({dname, ...props}) => (
  <TreeNode {...props} node={{datatype: 10, dname}} />
)

// also see useTreeNodes in db/hooks
export const TreeNode = ({node, link = false, linkLazy = false, title, style, as = 'span'}) => {
  /* build a (linked) TypeCaption given a node={otid, odid, datatype, subtype} structure
  link can be `true` or classname/tabname routing segment
  */
  let content =
    node.datatype === 10 ? (
      <DomainLabel dname={node.dname} odid={node.odid} style={style} as={as} title={title} />
    ) : (
      <TypeCaption {...node} style={style} as={as} title={title} />
    )
  if (link || linkLazy) {
    let id = node.otid
    let [classname, tabname, ctxid, query, updateQuery] = Array(5).fill(undefined)
    if (isString(link)) {
      // fetch classname/tabname from link='custom/view'
      ;[classname, tabname] = link.split('/')
    } else if (isObject(link)) {
      // XXX: unpacking?
      classname = link.classname
      tabname = link.tabname
      ctxid = link.ctxid
      query = link.query
      updateQuery = link.updateQuery
      if (link.id === null) {
        id = null
      }
    }

    return (
      <Link
        dname={node.dname}
        odid={node.odid}
        classname={classname}
        tabname={tabname}
        ctxid={ctxid}
        id={id}
        query={query}
        updateQuery={updateQuery}
        lazy={linkLazy}
        style={style}
      >
        {content}
      </Link>
    )
  }
  return content
}

const decode_cfgcols = (query, cfg) => {
  let all_names = flatten(
    mapItems(cfg, (n, cfg_data) => {
      if (!isObject(cfg_data)) return [n]
      // second level cfg
      return keys(cfg_data).map(k => `${n}.${k}`)
    })
  )
  let query_parts = query.split(',')
  //~ log.x('XX', cfgcols)
  return flatten(
    query_parts.map(qp => all_names.filter(n => qp.length > 2 && n.toLowerCase().includes(qp)))
  )
}
export const TypesConfigTable = ({
  datatypeName = 'metadata',
  cfgcols,
  query,
  withCounts = false,
  hideEmptyCounts = false,
  xdtypes,
}) => {
  let dtypes = useSelector(Sel.types)
  if (xdtypes) {
    dtypes = xdtypes
  }
  let dt_cfg = dtypes[datatypeName]

  let i18n = useSelector(Sel.i18n)
  let {summaries} = useSummaries(withCounts ? ['metadata'] : [])
  let metadataStats = summaries.metadata
  if (isString(cfgcols)) {
    cfgcols = cfgcols.split(',').map(e => strip(e))
  } else if (query) {
    cfgcols = decode_cfgcols(query, values(dt_cfg)[0])
  } else {
    cfgcols = []
  }

  let headergroups = null
  if (cfgcols.length > 1) {
    let colgroups = groupBy(cfgcols, e => e.split('.')[0])

    headergroups = (
      <Table.Row>
        <Table.HeaderCell />
        {mapItems(colgroups, (group, elements) => (
          <Table.HeaderCell key={group} colspan={elements.length}>
            <b>{group}</b>
          </Table.HeaderCell>
        ))}
      </Table.Row>
    )
  }
  let header = (
    <Table.Row>
      <Table.HeaderCell />
      {cfgcols.map(cfg_name => (
        <Table.HeaderCell key={cfg_name}>
          {headergroups ? cfg_name.split('.').slice(-1)[0] : cfg_name}
        </Table.HeaderCell>
      ))}
      {withCounts && <Table.HeaderCell>Anzahl</Table.HeaderCell>}
    </Table.Row>
  )

  let renderValue = v => {
    let s = String(v)
    return <tt>{s}</tt>
  }

  let rows = mapItems(
    dt_cfg || {},
    (name, cfg) => {
      let cells = cfgcols.map(cfg_name => (
        <Table.Cell key={cfg_name}>{renderValue(get(cfg, cfg_name))}</Table.Cell>
      ))
      let label = i18n.getType(name)

      let count = metadataStats?.[cfg.internalType]?.total
      let available = count ? true : false
      let availableStyle = available ? null : {color: 'var(--vl_bodySecondary_color-hover)'}
      return [
        label,
        <Table.Row disabled={!available} key={name}>
          <Table.Cell>
            <span style={availableStyle} title={`subtype ${cfg.internalType}`}>
              <TypeIcon name={name} /> {label}
            </span>
          </Table.Cell>
          {cells}
          {withCounts && <Table.Cell>{count}</Table.Cell>}
        </Table.Row>,
      ]
    },
    (name, cfg) => !hideEmptyCounts || metadataStats?.[cfg.internalType]?.total > 0
  )
  rows = sorted(rows, e => e[0]).map(e => e[1])
  return (
    <Table definition compact="very">
      <Table.Header>
        {headergroups}
        {header}
      </Table.Header>
      <Table.Body>{rows}</Table.Body>
    </Table>
  )
}

export const DatatypeConfigTable = ({dtcfg}) => {
  if (!dtcfg) return null
  let renderValue = v => {
    return String(v)
  }

  let renderScalars = (sec, sec_name = '') =>
    mapItems(
      sec,
      (k, v) => (
        <Table.Row key={k}>
          <Table.Cell style={{paddingLeft: sec_name ? '30px' : undefined}}>
            <tt>{k}</tt>
          </Table.Cell>
          <Table.Cell>{renderValue(v)}</Table.Cell>
        </Table.Row>
      ),
      (k, v) => !isObject(v)
    )

  let renderSecs = sec =>
    mapItems(
      sec,
      (k, v) => (
        <React.Fragment key={k}>
          <Table.Row>
            <Table.Cell colSpan="2" style={{backgroundColor: 'rgba(0,0,0,0.05)'}}>
              <tt>{k}</tt>
            </Table.Cell>
          </Table.Row>
          {renderScalars(v, k)}
        </React.Fragment>
      ),
      (k, v) => isObject(v)
    )
  let table = rows => (
    <Table definition compact="very">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Wert</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>{rows}</Table.Body>
    </Table>
  )

  return (
    <RGrid stackable>
      <RGrid.Row>
        <RGrid.Column width="8">{table(renderScalars(dtcfg))}</RGrid.Column>
        <RGrid.Column width="8">{table(renderSecs(dtcfg))}</RGrid.Column>
      </RGrid.Row>
    </RGrid>
  )
}
